import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/button';
import DemoDashboard from '../components/feature/demo-dashboard';
import styles from './home.module.css';

interface HomeProps {
  currentStage: number;
  onNextStage: () => void;
  onOpenModal: () => void;
}

const Home: React.FC<HomeProps> = ({ currentStage, onNextStage, onOpenModal }) => {
  const navigate = useNavigate();
  
  return (
    <div className={styles.pageWrapper}>
      <section className={styles.hero}>
        <div className="container">
          <div className={styles.heroContent}>
            <div style={{ paddingRight: '24px' }}>
              <h1 className={styles.heroTitle}>
                The Financial Intelligence Layer for Every Investor
              </h1>
              <p className={styles.heroSubtitle}>
                We translate global events into personalized intelligence for your portfolio. Your lens on the market. Smarter Decisions.
              </p>
              <div className={styles.heroActions}>
                <Button onClick={onOpenModal}>Join Briqq</Button>
                <Button variant="secondary" onClick={() => navigate('/how-it-works')}>See How It Works</Button>
              </div>
            </div>
            
            <DemoDashboard currentStage={currentStage} onNextStage={onNextStage} />
          </div>
        </div>
      </section>

      <section className={styles.features}>
        <div className="container">
          <h2 className={styles.featuresTitle}>The Financial Intelligence Layer for Every Investor</h2>
          <p className={styles.featuresSubtitle}>
            Three core capabilities that transform overwhelming noise into actionable intelligence
          </p>
          
          <div className={styles.featuresGrid}>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>🛡️</div>
              <h3 className={styles.featureTitle}>Risk Intelligence</h3>
              <p className={styles.featureDescription}>
                Get your personalized risk score (0-100) that reveals hidden portfolio vulnerabilities before they impact your returns. See sector concentration, macro exposure, and volatility in one clear number.
              </p>
            </div>
            
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>⚡</div>
              <h3 className={styles.featureTitle}>Daily Market Intelligence</h3>
              <p className={styles.featureDescription}>
                We cut through the chaos to surface the signals tied to your portfolio. No filler, no distractions.
              </p>
            </div>
            
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>🎯</div>
              <h3 className={styles.featureTitle}>Smart Personalization</h3>
              <p className={styles.featureDescription}>
                Your portfolio profile drives everything. Risk tolerance, investment horizon, and sector preferences shape every insight and alert, keeping you in the driver seat.
              </p>
            </div>
          </div>

          <div className={styles.featuresHighlights}>
            <h3 className={styles.featuresHighlightsTitle}>What We Do Best</h3>
            <div className={styles.featuresHighlightsGrid}>
              <div className={styles.featuresHighlight}>
                <h4 className={styles.featuresHighlightTitle}>🎯 Interpretation over Information</h4>
                <p className={styles.featuresHighlightText}>While others flood you with data, we explain what it means for YOUR portfolio</p>
              </div>
              <div className={styles.featuresHighlight}>
                <h4 className={styles.featuresHighlightTitle}>🌐 Signal-to-Portfolio Engine</h4>
                <p className={styles.featuresHighlightText}>The only platform connecting global market signals to real-time impact on your portfolio</p>
              </div>
              <div className={styles.featuresHighlight}>
                <h4 className={styles.featuresHighlightTitle}>📡 Signal-to-Noise Optimization</h4>
                <p className={styles.featuresHighlightText}>Daily signals that matter vs. endless feeds of irrelevant information</p>
              </div>
            </div>
            <div className={styles.featuresQuote}>
              <h3 className={styles.featuresQuoteText}>"Everyone else gives you more data. We give you better decisions."</h3>
              <p className={styles.featuresQuoteSubtitle}>
                We're the only platform that answers the daily question: <strong>"What does today's world mean for my money?"</strong>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 